import { useContext } from 'react'
import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { object, func } from 'prop-types'
import { CvsAutoComplete } from '@components'
import { listContext } from '@contexts'
import { excludeTypes } from '@global/constants'
import { RangeExact } from '../range-exact'
import { Transform } from '../transform'
import { t } from '@lingui/core/macro'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'

const TextFieldMarginLeft = styled(TextField)`
  margin-left: 8px;
`

const MarginTopTypography = styled(Typography)`
  margin-top: 12px;
`

export const Text = ({ form, setForm, disabled }) => {
  const { byId, fetchLists } = useContext(listContext)

  if (!Object.values(byId).length) {
    fetchLists()
  }

  const selectedProhibitions = []
  if (form.validation?.prohibit_letters) {
    selectedProhibitions.push('prohibit_letters')
  }
  if (form.validation?.prohibit_numbers) {
    selectedProhibitions.push('prohibit_numbers')
  }
  if (form.validation?.prohibit_spaces) {
    selectedProhibitions.push('prohibit_spaces')
  }
  if (form.validation?.prohibit_symbols) {
    selectedProhibitions.push('prohibit_symbols')
  }

  const excludeOptions = Object.keys(excludeTypes).map(type => ({
    label: excludeTypes[type],
    value: type,
  }))

  return (
    <>
      <FormControlLabel
        name="show-previous-values"
        control={
          <Checkbox
            disabled={disabled}
            onChange={({ target: { checked } }) =>
              setForm(prev => ({
                ...prev,
                validation: { ...prev.validation, show_previous: checked },
              }))
            }
            checked={Boolean(form.validation.show_previous)}
          />
        }
        label={t`Show previous values as list`}
      />
      <FormControlLabel
        name="flag-new-value"
        control={
          <Checkbox
            disabled={disabled}
            onChange={({ target: { checked } }) =>
              setForm(prev => ({
                ...prev,
                validation: { ...prev.validation, flag_new: checked },
              }))
            }
            checked={Boolean(form.validation?.flag_new)}
          />
        }
        label={t`Flag new value`}
      />
      <FormControlLabel
        name="list-for-suggestions"
        control={
          <Checkbox
            disabled={disabled}
            onChange={({ target: { checked } }) =>
              setForm(prev => ({
                ...prev,
                validation: { ...prev.validation, suggestion_list: checked },
              }))
            }
            checked={Boolean(form.validation?.suggestion_list)}
          />
        }
        label={t`Use list to provide suggestions`}
      />
      {form.validation.suggestion_list != 0 && form.validation.suggestion_list && (
        <div>
          <CvsAutoComplete
            label={t`Select list`}
            margin="dense"
            options={Object.values(byId)
              .filter(list => list.type === 'drop-down')
              .map(list => {
                return {
                  label:
                    list?.count > 100000
                      ? `${list.name} (too large to be used in suggestion)`
                      : list.name,
                  value: list.id,
                  isDisabled: list?.count > 100000,
                }
              })}
            selected={form.validation.suggestion_list}
            onSelected={item => {
              setForm({ ...form, validation: { ...form.validation, suggestion_list: item } })
            }}
          />
        </div>
      )}
      <MarginTopTypography variant="subtitle1">{t`Rules`}</MarginTopTypography>
      <FormControl>
        <Box display="inline-flex" justify-content="space-between">
          <TextField
            disabled={disabled}
            variant="outlined"
            name="beginsWith"
            value={form.validation.begins_with}
            onChange={({ target: { value } }) =>
              setForm(prev => ({
                ...prev,
                validation: { ...prev.validation, begins_with: value },
              }))
            }
            label={t`Begins with`}
            margin="dense"
          />
          <TextFieldMarginLeft
            disabled={disabled}
            variant="outlined"
            name="endsWith"
            value={form.validation.ends_with}
            onChange={({ target: { value } }) =>
              setForm(prev => ({
                ...prev,
                validation: { ...prev.validation, ends_with: value },
              }))
            }
            label={t`Ends with`}
            margin="dense"
          />
        </Box>
        <TextField
          disabled={disabled}
          variant="outlined"
          name="mustContain"
          placeholder={t`Must contain`}
          value={form.validation.contains}
          onChange={({ target: { value } }) =>
            setForm(prev => ({
              ...prev,
              validation: { ...prev.validation, contains: value },
            }))
          }
          label={t`Must contain`}
          margin="dense"
        />
        <CvsAutoComplete
          name="exclude"
          disabled={disabled}
          margin="dense"
          label={t`Exclude`}
          isMulti
          options={excludeOptions}
          onSelected={types => {
            const updatedForm = {
              ...form,
              validation: {
                ...form.validation,
                prohibit_letters: false,
                prohibit_numbers: false,
                prohibit_spaces: false,
                prohibit_symbols: false,
              },
            }
            types.forEach(type => {
              updatedForm.validation[type] = true
            })
            setForm(updatedForm)
          }}
          selected={formatSelectedOptions(true, selectedProhibitions, excludeOptions)}
        />
        <TextField
          disabled={disabled}
          variant="outlined"
          name="customExclusion"
          placeholder={t`Custom exclusion`}
          value={form.validation.does_not_contain}
          onChange={({ target: { value } }) =>
            setForm(prev => ({
              ...prev,
              validation: { ...prev.validation, does_not_contain: value },
            }))
          }
          label={t`Custom exclusion`}
          margin="dense"
        />
        {form.validation.does_not_contain && (
          <FormControlLabel
            name="excludeAsTerm"
            control={
              <Checkbox
                disabled={disabled}
                onChange={({ target: { checked } }) =>
                  setForm(prev => ({
                    ...prev,
                    validation: { ...prev.validation, exclude_as_string: checked },
                  }))
                }
                checked={Boolean(form.validation.exclude_as_string)}
              />
            }
            label={t`Exclude as term`}
          />
        )}
        <Transform setForm={setForm} form={form} disabled={disabled} />
        <RangeExact setForm={setForm} form={form} disabled={disabled} />
      </FormControl>
    </>
  )
}

Text.propTypes = {
  form: object.isRequired,
  setForm: func.isRequired,
}
